/**
 * PostHog Events for Narrator Score Chase System
 *
 * Track narrator reactions, floating pill interactions, and session engagement
 * during the rewrite score chase loop.
 *
 * Extracted from narrator-events.ts to keep files under 300 lines.
 *
 * Analytics Anti-Pattern Compliance:
 * - All event names are English constants (NO t() translations)
 * - All property values are English constants or primitives
 *
 * @module analytics/events/narrator-score-chase-events
 */

import { captureEvent } from '../../analytics';
import { NARRATOR_EVENTS } from './narrator-events';

// ============================================================================
// Score Chase Reaction Event Types
// ============================================================================

/**
 * Properties for Narrator_Reaction_Shown event
 */
interface ReactionShownProperties {
  /** What triggered this reaction */
  trigger: 'accept' | 'crossing' | 'loading' | 'complete' | 'rescore_result' | 'rescore_failure';
  /** User's subscription tier */
  tier: string;
  /** Animation energy intensity */
  energy_level: 'calm' | 'engaged' | 'excited' | 'peak';
  /** Cumulative score improvement so far */
  cumulative_delta: number;
  /** How many reactions have been shown this session */
  reaction_index: number;
  /** Whether the narrator card is visible in viewport */
  is_card_visible: boolean;
}

/**
 * Properties for Narrator_Pill_Shown event
 */
interface PillShownProperties {
  /** What triggered the underlying reaction */
  trigger: string;
  /** User's subscription tier */
  tier: string;
  /** Truncated preview of text shown in pill */
  reaction_preview: string;
}

/**
 * Properties for Narrator_Pill_Tapped event
 */
interface PillTappedProperties {
  /** What triggered the underlying reaction */
  trigger: string;
  /** User's subscription tier */
  tier: string;
  /** Time from pill appearing to tap (ms) */
  time_since_pill_shown_ms: number;
}

/**
 * Properties for Narrator_Session_Summary event
 */
interface SessionSummaryProperties {
  /** Total reactions shown during session */
  reactions_shown_count: number;
  /** Total pills shown during session */
  pills_shown_count: number;
  /** Total pills tapped during session */
  pills_tapped_count: number;
  /** Highest energy level reached */
  energy_peak_reached: string;
  /** Total session duration (ms) */
  session_duration_ms: number;
  /** Total score improvement across session */
  total_delta: number;
  /** Number of rewrites accepted */
  rewrites_accepted: number;
  /** Total rewrites available */
  rewrites_total: number;
}

// ============================================================================
// Score Chase Reaction Capture Functions
// ============================================================================

/**
 * Capture Narrator_Reaction_Shown event
 */
export function captureNarratorReactionShown(
  properties: ReactionShownProperties
): void {
  captureEvent(NARRATOR_EVENTS.REACTION_SHOWN, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Pill_Shown event
 */
export function captureNarratorPillShown(
  properties: PillShownProperties
): void {
  captureEvent(NARRATOR_EVENTS.PILL_SHOWN, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Pill_Tapped event
 */
export function captureNarratorPillTapped(
  properties: PillTappedProperties
): void {
  captureEvent(NARRATOR_EVENTS.PILL_TAPPED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Session_Summary event
 */
export function captureNarratorSessionSummary(
  properties: SessionSummaryProperties
): void {
  captureEvent(NARRATOR_EVENTS.SESSION_SUMMARY, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}
